import { useState } from 'react'
import { router } from '@inertiajs/react'
import { EllipsisHorizontalIcon } from '@heroicons/react/20/solid'
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
} from '@/components/ui'
import { useTranslations } from '@/lib/i18n'

export interface RowAction {
  label: string
  href?: string
  onClick?: () => void
}

/**
 * Compact overflow (⋯) menu for table row actions — keeps action columns tidy
 * and consistent across Merchants/Users. A `destroy` action is rendered in red
 * and gated behind a confirm dialog (Inertia DELETE).
 */
export function RowActions({
  items = [],
  destroy,
  menuLabel,
}: {
  items?: RowAction[]
  destroy?: { url: string; label: string; confirm: string }
  menuLabel: string
}) {
  const { t } = useTranslations()
  const [confirming, setConfirming] = useState(false)

  if (items.length === 0 && !destroy) return null

  return (
    <>
      <Dropdown>
        <DropdownButton plain aria-label={menuLabel}>
          <EllipsisHorizontalIcon className="size-5" />
        </DropdownButton>
        <DropdownMenu anchor="bottom end">
          {items.map((item) => (
            <DropdownItem key={item.label} href={item.href} onClick={item.onClick}>
              {item.label}
            </DropdownItem>
          ))}
          {destroy && (
            <DropdownItem onClick={() => setConfirming(true)} className="text-status-failed-fg">
              {destroy.label}
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>

      {destroy && (
        <Dialog open={confirming} onClose={setConfirming}>
          <DialogTitle>{destroy.confirm}</DialogTitle>
          <DialogActions>
            <Button plain onClick={() => setConfirming(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              color="red"
              onClick={() => {
                setConfirming(false)
                router.delete(destroy.url)
              }}
            >
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
